import userManager from '@/components/oidc';

export const adminRoles = ['userAdmin', 'orgSuperAdmin', 'superAdmin'] as const;
export type AdminRole = typeof adminRoles[number];
export const adminLevels = ['none', ...adminRoles] as const;
export type AdminLevel = typeof adminLevels[number];
export const syncFlags = ['undecided', 'sync', 'block'];
export type SyncFlag = typeof syncFlags[number];
export type HistoryEventCode = 'CREATED' | 'VERIFIED' | 'LAST_LOGIN' | 'LAST_CHANGE';
export type LicenseCategory = 'tech' | 'academy' | 'business';

/**
 * convert response received with Fetch API to json object
 */
const toJson = async (resp: Response) => {
  if (!resp?.ok) throw await BackendError.create(resp);

  if (resp.headers.get('content-type') === 'application/json') return await resp.json();
  return resp;
};

export const levelOf = (level?: AdminLevel) => (level ? adminLevels.indexOf(level) : 'none');

export enum BusinessType {
  Dealer = 100000000,
  ProspectDealer = 100000001,
  SubDealer = 100000002,
  CommercialCustomer = 100000003,
  Other = 100000004,
  ServicePartnerWithoutPOS = 100000005,
  Subsidiary = 100000006,
  Importer = 100000007,
  Representation = 100000008,
  CompetitorStore = 100000009,
  RentalPartnerWithoutPOS = 100000010,
  DoItYourself = 100000012,
  OnlinePureplay = 100000013,
  FarmAndRanch = 100000015,
  HardwareStore = 100000016,
  OtherSalesChannels = 100000017,
}

/**
 * generic query options/parameters (odata inspired)
 */
export type QueryOptions = {
  $select?: string;
  $filter?: string;
  $search?: string;
  $expand?: string;
  $top?: number;
  $locale?: string;
}

const toURLParams = (opt?: Record<string, string | number | boolean | string[] | undefined>): string => {
  if (!opt) return '';
  const params = new URLSearchParams();
  Object.keys(opt).forEach((key) => {
    // {false} is a valid value for a parameter
    if (opt[key] !== null && opt[key] !== undefined) params.append(key, String(opt[key]));
  });
  return params.toString();
};

// similar to AxiosError packs an error response
export class BackendError {
  response: Response;
  restError?: RestError;

  constructor(response: Response, restError?: RestError) {
    this.response = response;
    this.restError = restError;
  }

  static async create(response: Response) {
    if (!response.ok && response.headers.get('content-type') === 'application/json') {
      const err = await response.json();
      return new BackendError(response, err);
    }
    return new BackendError(response);
  }
}

export interface RestResponse<DATA, EXTEND> {
  data: DATA;
  complete?: boolean;
  extra?: EXTEND;
}

export interface RestError {
  code: string;
  message?: string;
  invalidField?: string; // only if code is "INVALID_PARAMETER"
}

export type UserFilter = {
  $search?: string;
  'organizations[]'?: string[];
  'adminLevel[]'?: AdminLevel[];
  'syncFlag[]'?: SyncFlag[];
  verified?: boolean;
  email?: string;
}

export type PINType = 'CPF'; // can add later more types (for different countries)

export interface PIN {
  type: PINType;
  value: string;
}

export class CPF implements PIN {
  type: PINType = 'CPF';
  value: string;

  constructor(value: string) {
    this.value = value;
  }
}

export interface UserData {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  confirmed: boolean;
  roles?: string[];
  organization: string; // ref OrganizationData
  licenses: string[];
  preferredLanguage?: string;
  pin?: PIN;
  birthdate?: string;

  adminLevel: AdminLevel;
  managedOrganizations?: string[];
  managedAsPrimaryContact?: string[];

  syncFlag?: boolean;
  crmId?: string;

  lastLogin?: string;
  history?: HistoryItem[];
}

export interface AdminDTO {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  adminRole: AdminRole;
}

export const userDisplayName = (user: UserData) => `${user.firstname} ${user.lastname}`;

export type OrgState = 'active' | 'inactive';

// @Deprecated
export interface OrganizationData {
  id: string;
  name: string;
  crmNo: string;
  state?: OrgState;
}

export const orgDisplayName = (org: OrganizationData) => `${org.name} ${org.crmNo}`;

/**
 * map: {locale: translation}
 */
export type AttributeI18nValues = Record<string, string>; // limit to supported locales?

export interface LicenseData {
  id: string;
  name: string;
  displayName: string;
  description: string;
  category: LicenseCategory;
}

export interface LicenseDefinition {
  // id: string;
  name: string;
  displayName: AttributeI18nValues;
  description: AttributeI18nValues;
}

/// localized business-role ready to be displayed
export interface BusinessRole {
  id: string;
  name: string;
  description: string;
}

// business-role with all the translations
export interface BusinessRoleDefinition {
  id: string;
  name: AttributeI18nValues;
  description: AttributeI18nValues;
}

export interface OrganizationDetails extends OrganizationData {
  licenses?: string[]; // ref LicenseData
  countries?: string[]; // ISO3 format; example: ["DEU","BRA"]
  admins?: string[]; // ref AdminSummary
  primaryContact: string;
  createdOn?: string;
  modifiedOn?: string;
  parent?: string;
  businessType?: BusinessType;
  state?: OrgState;
}

export type ChangeType = 'replace' | 'remove';

export interface FieldTranslationChange {
  operation: ChangeType;
  field: string;
  locale: string;
  translation: string;
}

export interface UserExtendedData {
  organization?: OrganizationDetails[];
}

export interface HistoryItem {
  timestamp: string; // pattern = "dd.MM.yyyy hh:mm:ss"
  code: HistoryEventCode;
  context?: Record<string, string>;
}

export interface MyPreferences {
  ddcNotification?: boolean;
  hideTutorials?: boolean;
}

/**
 * updatable user attributes
 */
export type UserAttr =
// 'salutation' |
  'firstname'
  | 'lastname'
  | 'preferredLanguage'
  | 'birthdate'
  | 'pin'
  | 'roles'
  | 'syncFlag'
  | 'crmId'
  | 'organization'
  | 'licenses'
  | 'adminRole'
  | 'managedOrganizations';

export type State = 'active' | 'inactive';

export interface Change<T> {
  op: ChangeType;
  field: T;
  value?: string | string[];
}

export interface ChangeUsersPasswordRequest {
  myPassword: string;
  newPassword: string;
}

export interface SearchCrmContactsRequest extends Record<string, string | undefined> {
  organization?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  state?: State;
}

export interface SearchCrmAccountsRequest {
  id?: string;
  crmNo?: string;
  name?: string;
  state?: State;
}

export interface CrmContact {
  crmId: string;
  webssoId?: string;
  email: string;
  firstname: string;
  lastname: string;
  preferredLanguage?: string;
  roles?: string[];
  orgId: string;
  orgName?: string;
  taxNumber?: string;
  inactive: boolean;
}

export interface CrmAccount {
  id: string;
  crmNo: string;
  name: string;
  published?: string;
  parent?: string;
  businessType?: string;
  country?: string;
  primaryAdmin?: string;
  state?: State;
}

export interface CreateUserRequest {
  email: string;
  firstname: string;
  lastname: string;
  organization: string;
  pin?: PIN;

  adminLevel?: AdminLevel;
  managedOrganizations?: string[];

  roles?: string[];
  licenses?: string[];
  preferredLanguage?: string;
  crmId?: string;
  syncFlag?: boolean;
}

export interface OrganizationExtendedData {
  licenses?: LicenseData[];
  admins?: AdminDTO[];
  parent?: OrganizationData[];
}

export const AppGroups = [
  { id: 'group-ssc-api', name: 'SSC API', scope: 'ssc-api', audience: 'ssc' },
  { id: 'group-inventory-api', name: 'Dealers Inventory API', scope: 'upload.write', audience: 'dealer-inventory-api' },
  { id: 'group-b2b-api', name: 'B2B API', scope: 'b2b-api', audience: 'b2b' },
];

export type DynClientType = typeof AppGroups[number]['id'];

export interface Oauth2App {
  id: string;
  password?: string;
  name: string;
  contacts: string[];
  //
  organization: string;
  group: DynClientType;
  //
  history: AuditActivity[];
  //
  active: boolean;
}

export interface AuditActivity {
  timestamp: string | Date;
  subject: string;
  activity: 'create' | 'update' | 'pwReset';
  object?: string;
}

export interface SaveAppRequest {
  name: string;
  organization: string;
  contacts?: string[];
}

/**
 * REST client for communicating with the DDC-API
 */
export default class Backend {
  url = import.meta.env.VITE_BACKEND;

  abortController?: AbortController;

  fetchConfig: Record<string, any>;

  public constructor(accessToken: string) {
    this.fetchConfig = {
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json',
      }
    };
  }

  /**
   * builds a Backend instance if there is a valid access_token available
   */
  static async validateToken(): Promise<Backend> {
    const u = await userManager.getValidUser();
    return new Backend(u.access_token);
  }

  withAbortController(ac: AbortController): Backend {
     this.abortController = ac; // can be used to interrupt the request
    this.fetchConfig.signal = ac.signal;
     return this;
  }

  // findUsers = async (opt: QueryOptions, filter?: UserFilter):
  //   Promise<AxiosResponse<RestResponse<UserData[], UserExtendedData>>> => (
  //   filter
  //     // v2
  //     ? axios.get(`${this.url}/v2/users`, {
  //       params: { ...opt, ...filter },
  //       ...this.defaultConfig,
  //       signal: this.abortController ? this.abortController.signal : undefined,
  //       // paramsSerializer: params => new URLSearchParams(params).toString()
  //     })
  //     // v2.1 - odata like
  //     : axios.get(`${this.url}/v3/users`, {
  //       params: { ...opt },
  //       ...this.defaultConfig,
  //       signal: this.abortController ? this.abortController.signal : undefined,
  //     })
  // );

  findUsers = async (opt: QueryOptions, filter?: UserFilter):
    Promise<RestResponse<UserData[], UserExtendedData>> => (
    filter
      // v2
      ? fetch(`${this.url}/v2/users?${toURLParams({ ...opt, ...filter })}`, this.fetchConfig).then(toJson)
      // v2.1 - odata like
      : fetch(`${this.url}/v3/users?${toURLParams(opt)}`, this.fetchConfig).then(toJson)
  );

  downloadUsers = async (filter: UserFilter) => (
    fetch(`${this.url}/v2/users?${toURLParams(filter)}`, {
      headers: { ...this.fetchConfig.headers, Accept: 'application/octet-stream,*/*' }
    })
  );

  loadUser = async (id: string, opt?: QueryOptions):
    Promise<RestResponse<UserData, UserExtendedData>> => (
    fetch(`${this.url}/v2/users/${id}?${toURLParams({ id, ...opt })}`, this.fetchConfig).then(toJson)
  );

  userExists = async (email?: string, cpf?: string): Promise<boolean> => {
    if (!email && !cpf) throw new Error('email or cpf is required');
    const param = email ? `email=${encodeURIComponent(email)}` : `cpf=${encodeURIComponent(cpf!)}`;
    const res = await fetch(`${this.url}/v3/users?${param}`, {
      ...this.fetchConfig,
      method: 'HEAD',
    });
    if (res.status === 204) return true;
    if (res.status === 404) return false;
    throw new Error(`Unexpected status ${res.status} checking user existence`);
  };

  loadOrganization = async (id: string, opt?: QueryOptions):
    Promise<RestResponse<OrganizationDetails, OrganizationExtendedData>> => (
    fetch(`${this.url}/v2/organizations/${id}?${toURLParams({ id, ...opt })}`, this.fetchConfig).then(toJson)
  );

  // deprecated - use /v3
  getActiveRoles = async (id: string): Promise<RestResponse<string[], undefined>> => (
    fetch(`${this.url}/v2/organizations/${id}/active-roles`, this.fetchConfig).then(toJson)
  );

  getActiveRolesV3 = async (id: string, locale: string): Promise<RestResponse<BusinessRole[], undefined>> => (
    fetch(`${this.url}/v3/organizations/${id}/active-roles?$locale=${locale}`, this.fetchConfig).then(toJson)
  );

  createUser = async (req: CreateUserRequest): Promise<RestResponse<string, undefined>> => (
    this.submit(`${this.url}/v2/users`, 'POST', req)
  );

  resendVerificationEmail = async (uid: string) => (
    this.submit(`${this.url}/v2/users/${uid}/verification-email`, 'POST')
  );

  /// shortcut for editable calls
  private submit = async (url: string, method: 'POST' | 'PATCH' | 'DELETE', reqBody?: unknown) => (
    fetch(url, {
      ...this.fetchConfig,
      method,
      ...(!!reqBody && { body: JSON.stringify(reqBody) }),
    }).then(toJson)
  );

  updateUser = async (uid: string, req: Change<UserAttr>[]) => (
    this.submit(`${this.url}/v2/users/${uid}`, 'PATCH', req)
  );

  changeUsersPassword = async (uid: string, req: ChangeUsersPasswordRequest) => (
    this.submit(`${this.url}/v2/users/${uid}/password`, 'POST', req)
  );

  deleteUser = async (uid: string) => (
    this.submit(`${this.url}/v2/users/${uid}`, 'DELETE')
  );

  findOrganizations = async (params: QueryOptions):
    Promise<RestResponse<OrganizationData[], OrganizationExtendedData>> => (
    fetch(`${this.url}/v2/organizations?${toURLParams(params)}`, this.fetchConfig).then(toJson)
  );

  moveUsers = async (src: string, dest: string): Promise<RestResponse<number, undefined>> => (
    this.submit(`${this.url}/v2/organizations/${dest}/users?source=${src}`, 'POST')
  );

  saveMyPreferences = async (req: MyPreferences) => (
    this.submit(`${this.url}/v2/preferences`, 'POST', req)
  );

  getMyPreferences = async (): Promise<MyPreferences> => (
    fetch(`${this.url}/v2/preferences`, this.fetchConfig).then(toJson)
  );

  /// CRM ----------------------------------------------------------------------

  searchCrmContacts = async (params: SearchCrmContactsRequest, fields?: string): Promise<CrmContact[]> => {
    const top = window.APP_CONFIG.crm.cdh_contacts_top as number;
    return fetch(`${this.url}/v2/crm/contacts?${toURLParams({ ...params, fields, top })}`, { ...this.fetchConfig }).then(toJson);
  };

  getCrmContact = async (id: string, fields?: string): Promise<CrmContact> => (
    fetch(`${this.url}/v2/crm/contacts/${id}?${toURLParams({ fields })}`, this.fetchConfig).then(toJson)
  );

  searchCrmAccounts = async (params: SearchCrmAccountsRequest, fields?: string): Promise<CrmAccount[]> => (
    fetch(`${this.url}/v2/crm/accounts?${toURLParams({ ...params, fields })}`, this.fetchConfig).then(toJson)
  );

  searchCrmAccountLicenses = async (id: string): Promise<string[]> => (
    fetch(`${this.url}/v2/crm/accounts/${id}/licenses?${toURLParams({ id })}`, this.fetchConfig).then(toJson)
  );

  /// LICENSES ----------------------------------------------------------------

  loadLicenses = async (): Promise<RestResponse<LicenseData[], void>> => (
    fetch(`${this.url}/v2/licenses`, this.fetchConfig).then(toJson)
  );

  loadLicense = async (id: string): Promise<RestResponse<LicenseDefinition, void>> => (
    fetch(`${this.url}/v2/licenses/${id}`, this.fetchConfig).then(toJson)
  );

  createLicense = async (dto: LicenseData) => (
    fetch(`${this.url}/v2/licenses`, { method: 'POST', body: JSON.stringify(dto), ...this.fetchConfig }).then(toJson)
  );

  updateLicenseTranslations = async (id: string, dto: FieldTranslationChange[]) => (
    fetch(`${this.url}/v2/licenses/${id}/translations`, { method: 'PATCH', body: JSON.stringify(dto), ...this.fetchConfig }).then(toJson)
  );

  deleteLicense = async (id: string) => (
    fetch(`${this.url}/v2/licenses/${id}`, { method: 'DELETE', ...this.fetchConfig }).then(toJson)
  );

  /// ROLES ------------------------------------------------------------------

  loadRoles = async (opt?: QueryOptions): Promise<RestResponse<BusinessRole[], void>> => (
    fetch(`${this.url}/v2/roles?${toURLParams(opt)}`, this.fetchConfig).then(toJson)
  );

  loadRole = async (id: string): Promise<RestResponse<BusinessRoleDefinition, void>> => (
    fetch(`${this.url}/v2/roles/${id}`, this.fetchConfig).then(toJson)
  );

  createRole = async (dto: BusinessRole) => (
    fetch(`${this.url}/v2/roles`, { method: 'POST', body: JSON.stringify(dto), ...this.fetchConfig }).then(toJson)
  );

  deleteRole = async (id: string) => (
    fetch(`${this.url}/v2/roles/${id}`, { method: 'DELETE', ...this.fetchConfig }).then(toJson)
  );

  updateRoleTranslation = async (id: string, dto: FieldTranslationChange[]) => (
    fetch(`${this.url}/v2/roles/${id}/translations`, { method: 'PATCH', body: JSON.stringify(dto), ...this.fetchConfig }).then(toJson)
  );

  /// APPLICATIONS ----------------------------------------------------------

  queryApplications = async (oid: string): Promise<RestResponse<Oauth2App[], void>> => (
    fetch(`${this.url}/v2/oauth2clients?oid=${oid}`, this.fetchConfig).then(toJson)
  );

  loadApplication = async (id: string): Promise<Oauth2App> => (
    fetch(`${this.url}/v2/oauth2clients/${id}`, this.fetchConfig).then(toJson)
  );

  createApp = async (req: SaveAppRequest): Promise<Oauth2App> => (
    fetch(`${this.url}/v2/oauth2clients`, {
      method: 'POST',
      body: JSON.stringify(req),
      ...this.fetchConfig
    }).then(toJson)
  );

  updateApp = async (id: string, req: SaveAppRequest): Promise<Oauth2App> => (
    fetch(`${this.url}/v2/oauth2clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(req),
      ...this.fetchConfig
    }).then(toJson)
  );

  regenerateAppSecret = async (id: string): Promise<Oauth2App> => (
    fetch(`${this.url}/v2/oauth2clients/${id}/secret`, {
      method: 'POST',
      ...this.fetchConfig
    }).then(toJson)
  );

  deleteApp = async (id: string) => (
    fetch(`${this.url}/v2/oauth2clients/${id}`, {
      method: 'DELETE',
      ...this.fetchConfig
    })
  );

  static errorMessage(e: BackendError, transl: (k: string) => (string)) {
    const code = e.restError?.code;
    if (code) {
      const msg = transl(`adm.error.${code}`);
      if (msg && msg !== `adm.error.${code}`) return msg;
    }
    return transl('adm.error.generic');
  }
}

